import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Badge, Button, Card, CardHeader, EmptyState, Tabs, Tag,
} from '@opendate-app/setlist';
import { attributionChannelColor } from '../../../shared/utils/attributionChannelColors';
import { formatTrackingDate } from '../../../ReportingDashboard/components/attribution/attributionReportUtils';

const VISIBLE_PAGES = 3;

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const mutedStyle = { color: 'var(--sl-text-tertiary)', fontSize: 13 };
const wrapStyle = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 };

export const touchPath = (rows) => [...rows].reverse().reduce((steps, row) => {
  const last = steps[steps.length - 1];
  if (last && last.channelKey === row.channelKey) {
    last.count += 1;
    return steps;
  }
  return [...steps, { channelKey: row.channelKey, channel: row.channel, count: 1 }];
}, []);

export const timelineItems = (rows, sessions) => [
  ...rows.map((row) => ({ kind: 'touch', key: `touch-${row.key}`, at: row.at, date: row.date, time: row.time, row })),
  ...sessions.map((session) => ({ kind: 'visit', key: `visit-${session.key}`, at: session.at, date: session.date, time: session.time, session })),
].sort((a, b) => {
  if ((a.at || '') !== (b.at || '')) return (b.at || '').localeCompare(a.at || '');
  if (a.kind === b.kind) return 0;
  return a.kind === 'touch' ? -1 : 1;
});

const ChannelBadge = ({ channelKey, children }) => (
  <Badge color={attributionChannelColor(channelKey)} dot>{children}</Badge>
);

ChannelBadge.propTypes = { channelKey: PropTypes.string, children: PropTypes.node.isRequired };

const details = (row) => [
  ['Source', row.source],
  ['Campaign', row.campaign],
  ['Page', row.landingPath],
  ['Visitor', row.visitor],
].filter(([, value]) => value);

const visitDotStyle = {
  width: 10, height: 10, borderRadius: '50%', border: '2px solid var(--sl-border-primary)', background: 'var(--sl-bg-primary)',
};

function TouchContent({ row }) {
  return (
    <>
      <div style={{ ...wrapStyle, marginBottom: 8 }}>
        <span style={{ fontWeight: 600 }}>{row.channel}</span>
        {row.surface && <span style={mutedStyle}>{row.surface}</span>}
      </div>
      <div style={wrapStyle}>
        {details(row).map(([label, value]) => (
          <Tag key={label}>
            <span style={{ color: 'var(--sl-text-tertiary)' }}>{label}</span>
            {' '}
            {value}
          </Tag>
        ))}
      </div>
    </>
  );
}

TouchContent.propTypes = { row: PropTypes.object.isRequired };

function VisitContent({ session }) {
  const [expanded, setExpanded] = useState(false);
  const { pages } = session;
  const collapsed = !expanded && pages.length > VISIBLE_PAGES;
  const shown = collapsed ? pages.slice(0, VISIBLE_PAGES - 1) : pages;

  return (
    <>
      <div style={{ ...mutedStyle, marginBottom: 8 }}>{plural(pages.length, 'page view')}</div>
      <ol className="list-unstyled m-0" style={{ borderLeft: '2px solid var(--sl-border-secondary)', paddingLeft: 16 }}>
        {shown.map((page) => (
          <li key={page.key} className="d-flex" style={{ gap: 12, alignItems: 'baseline', paddingBottom: 6 }}>
            <span>{page.title}</span>
            {page.path && <span style={{ ...mutedStyle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{page.path}</span>}
            <span className="ml-auto flex-shrink-0" style={mutedStyle}>{page.time}</span>
          </li>
        ))}
      </ol>
      {collapsed && (
        <Button variant="link-color" size="sm" onClick={() => setExpanded(true)}>
          {`Show ${pages.length - shown.length} more`}
        </Button>
      )}
    </>
  );
}

VisitContent.propTypes = { session: PropTypes.object.isRequired };

function ActivityTab({ timeline }) {
  const { rows } = timeline;
  const sessions = timeline.pageSessions || [];
  const [view, setView] = useState(sessions.length > 0 ? 'all' : 'touches');
  const path = touchPath(rows);
  const items = timelineItems(rows, view === 'all' ? sessions : []);

  return (
    <Card
      padding="none"
      header={(
        <CardHeader
          title="Touch timeline"
          supportingText={[
            timeline.trackingStartedAt ? `Tracking since ${formatTrackingDate(timeline.trackingStartedAt)}.` : 'Tracking hasn\'t started for this team yet.',
            timeline.truncatedAt && `Showing the most recent ${timeline.truncatedAt} touches.`,
            timeline.pageViewsTruncatedAt && `Showing the most recent ${timeline.pageViewsTruncatedAt} page views.`,
          ].filter(Boolean).join(' ')}
          actions={sessions.length > 0 && (
            <Tabs
              variant="button-border"
              size="sm"
              value={view}
              onChange={setView}
              items={[{ value: 'touches', label: 'Touches' }, { value: 'all', label: 'All activity' }]}
            />
          )}
        />
      )}
    >
      {items.length === 0 ? (
        <div className="p-4"><EmptyState size="sm" title="No tracked visits" /></div>
      ) : (
        <>
          <div className="px-4 py-3" style={{ ...wrapStyle, background: 'var(--sl-bg-secondary)', borderBottom: '1px solid var(--sl-border-secondary)' }}>
            <span style={mutedStyle}>Path</span>
            {path.map((step, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={index}>
                {index > 0 && <span aria-hidden="true" style={mutedStyle}>→</span>}
                <ChannelBadge channelKey={step.channelKey}>
                  {step.count > 1 ? `${step.channel} ×${step.count}` : step.channel}
                </ChannelBadge>
              </React.Fragment>
            ))}
            {timeline.pageViewCount > 0 && (
              <span className="ml-auto" style={mutedStyle}>
                {`${plural(timeline.pageViewCount, 'page view')} · ${plural(timeline.uniquePageCount, 'unique page')}`}
              </span>
            )}
          </div>
          <ol className="list-unstyled m-0 px-4 py-3">
            {items.map((item, index) => (
              <li key={item.key} className="d-flex" style={{ gap: 16 }}>
                <div className="text-right flex-shrink-0" style={{ width: 110, paddingBottom: 20 }}>
                  {item.date !== items[index - 1]?.date && <div style={{ fontWeight: 600 }}>{item.date}</div>}
                  <div style={mutedStyle}>{item.time}</div>
                </div>
                <div className="d-flex flex-column align-items-center flex-shrink-0" style={{ width: 24, paddingTop: 4 }}>
                  {item.kind === 'touch'
                    ? <Badge color={attributionChannelColor(item.row.channelKey)} dot aria-hidden="true" />
                    : <span aria-hidden="true" style={visitDotStyle} />}
                  <span aria-hidden="true" className="flex-grow-1" style={{ width: 2, marginTop: 4, background: 'var(--sl-border-secondary)' }} />
                </div>
                <div className="flex-grow-1" style={{ minWidth: 0, paddingBottom: 20 }}>
                  {item.kind === 'touch' ? <TouchContent row={item.row} /> : <VisitContent session={item.session} />}
                </div>
              </li>
            ))}
          </ol>
        </>
      )}
    </Card>
  );
}

ActivityTab.propTypes = {
  timeline: PropTypes.shape({
    rows: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      at: PropTypes.string,
      channelKey: PropTypes.string,
      channel: PropTypes.string,
      date: PropTypes.string,
      time: PropTypes.string,
    })).isRequired,
    pageSessions: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      at: PropTypes.string,
      date: PropTypes.string,
      time: PropTypes.string,
      pages: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        title: PropTypes.string,
        path: PropTypes.string,
        time: PropTypes.string,
      })).isRequired,
    })),
    pageViewCount: PropTypes.number,
    pageViewsTruncatedAt: PropTypes.number,
    uniquePageCount: PropTypes.number,
    trackingStartedAt: PropTypes.string,
    truncatedAt: PropTypes.number,
  }).isRequired,
};

export default ActivityTab;
